import React from 'react';
import { MDBBtn, MDBCard, MDBCardBody, MDBCheckbox, MDBCollapse, MDBDropdown, MDBDropdownItem, MDBDropdownMenu, MDBDropdownToggle, MDBTable, MDBTableBody, MDBTableHead } from 'mdb-react-ui-kit';
import Pager from './pager';
import Select, { type OptionType } from './select';
import { I18N } from '../util/i18n';
import InputBox from './input-box';
import { ListResponse } from '../api/types';
import { CancelablePromise } from '../util/client';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp, faFilter, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { willSendFile } from '../util/misc';

interface Column {
    label: string;
    editable?: (item: any, ref: RowRef, e: React.FocusEvent<HTMLTableCellElement>) => void;
    type?: React.HTMLInputTypeAttribute | 'select';
    filterable?: string;
    sortable?: string;
    format?: (value: any, item: Record<string, any>, ref: RowRef) => string | JSX.Element;
    hidden?: boolean;
    className?: string;
    emptyOption?: string;
    load?: () => Promise<Array<OptionType>>;
    options?: Array<OptionType>;
}

export type Columns = Record<string, Column>;

type FilterProps = {
    columns: Columns;
    onReset?: (search: URLSearchParams) => URLSearchParams;
    renderBtns?: (search: URLSearchParams) => JSX.Element | null;
    additionalFilter?: (search: URLSearchParams) => JSX.Element | null;
}

function Filter(props: FilterProps & { params: URLSearchParams, onSearch: (params: URLSearchParams) => void }) {
    const {0: status, 1: setStatus} = React.useState(false);
    const {0: data, 1: setData} = React.useState<Record<string, any>>({});
    const onSubmit = React.useCallback<React.FormEventHandler<HTMLFormElement>>(e => {
        e.stopPropagation();
        e.preventDefault();
        let fd = new FormData(e.target as HTMLFormElement);
        let params = new URLSearchParams(props.params);
        for (const [k, v] of fd.entries()) {
            params.set(k, v as string);
        }
        params.set('page', '1');
        props.onSearch(params);
    }, [props.onSearch, props.params]);
    const onReset = React.useCallback<React.FormEventHandler<HTMLFormElement>>(e => {
        e.stopPropagation();
        e.preventDefault();
        props.onSearch(props.onReset ? props.onReset(props.params) : new URLSearchParams());
        setData({});
    }, [props.onSearch, props.params, props.onReset]);
    const onChange = React.useCallback<{ (e: React.ChangeEvent<HTMLInputElement>): void; (e: string, v: string): void }>((e: React.ChangeEvent<HTMLInputElement> | string, v?: string) => {
        setData(old => {
            let cloned = { ...old };
            if (typeof e === 'string') {
                cloned[e] = v;
            } else {
                cloned[e.target.name] = e.target.value;
            }
            return cloned;
        });
    }, [setData]);
    let keys = React.useMemo(() => {
        let result = [];
        for (let k in props.columns) {
            if (props.columns[k].filterable) {
                result.push(k);
            }
        }
        return result;
    }, [props.columns]);
    React.useEffect(() => {
        let kv: Record<string, string> = {};
        for (const [k, v] of props.params.entries()) {
            kv[k] = v;
        }
        setData(kv);
    }, [props.params, setData]);
    return (
        <>
            <MDBCollapse open={status} className="filter">
                <form onSubmit={onSubmit} onReset={onReset}>
                    {keys.map(key => {
                        let name = props.columns[key].filterable || key;
                        let value = data[name] || '';
                        if (['date', 'datetime-local'].includes(props.columns[key].type ?? 'input') &&
                            !(new Date(value)).getTime()) {
                            value = '';
                        }
                        switch (props.columns[key].type) {
                            case 'select':
                                return (
                                    <Select key={key} name={name} value={value}
                                        options={props.columns[key].options}
                                        onChange={onChange.bind(null, name)}
                                        label={props.columns[key].label}
                                        load={props.columns[key].load}>
                                        <option value="">{props.columns[key].emptyOption || ''}</option>
                                    </Select>
                                );
                            default:
                                return (
                                    <InputBox key={key} label={props.columns[key].label}>
                                        <input type={props.columns[key].type || 'text'}
                                            className="form-control active" name={name}
                                            value={value} onChange={onChange} />
                                    </InputBox>
                                );
                        }
                    })}
                    {typeof props.additionalFilter === 'function' ? props.additionalFilter(props.params) : props.additionalFilter}
                    <MDBBtn type="submit"><I18N>Search</I18N></MDBBtn>
                    <MDBBtn type="reset" color="warning"><I18N>Reset</I18N></MDBBtn>
                    {typeof props.renderBtns === 'function' ? props.renderBtns(props.params) : props.renderBtns}
                </form>
            </MDBCollapse>
            <MDBBtn onClick={setStatus.bind(null, !status)} className="filter-toggle d-flex d-md-none">
                <span>
                    <FontAwesomeIcon icon={faFilter} className="me-2" />
                    <I18N>Filter</I18N>
                </span>
                <FontAwesomeIcon icon={status ? faCaretUp : faCaretDown} />
            </MDBBtn>
        </>
    );
}

type SorterProps = {
    columns: Columns;
    checkable?: (id: number | string) => Record<string, any>;
    onSort?: (search: URLSearchParams) => void;
}

function Sorter(props: SorterProps & { params: URLSearchParams, onSort: (params: URLSearchParams) => void, actions?: boolean, multiActions?: boolean }) {
    const onClick = React.useCallback((name: string, e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();
        let params = new URLSearchParams;
        if (props.params.get('sort') === name + ' DESC') {
            params.set('sort', name + ' ASC');
        } else {
            params.set('sort', name + ' DESC');
        }
        props.onSort && props.onSort(params);
    }, [props.params, props.onSort]);
    return (
        <tr>
            {props.multiActions || props.checkable ? (
                <th className="check">
                    <MDBCheckbox />
                </th>
            ) : null}
            {Object.keys(props.columns).map(key => {
                if (props.columns[key].hidden) {
                    return null;
                }
                let name = props.columns[key].sortable || key;
                return (
                    <th key={key} className={'text-nowrap ' + (props.columns[key].className || '')}>
                        {props.columns[key].sortable ? (
                            <MDBBtn color="link" onClick={onClick.bind(null, name)}>
                                {props.columns[key].label}
                                {props.params.get('sort') === name + ' DESC' ? (
                                    <FontAwesomeIcon icon={faCaretDown} className="ms-1" />
                                ) : (props.params.get('sort')?.startsWith(name) ? (
                                    <FontAwesomeIcon icon={faCaretUp} className="ms-1" />
                                ) : null)}
                            </MDBBtn>
                        ) : props.columns[key].label}
                    </th>
                );
            })}
            {props.actions ? (
                <th className="action text-center"><I18N>Action</I18N></th>
            ) : null}
        </tr>
    );
}

interface RowRef {
    remove: () => void;
    redraw: (item: Record<string, any>) => void;
}

export type Actions = Array<{
    label: string;
    onClick: (item: any, ref: RowRef, e: React.MouseEvent<HTMLElement>) => void;
    condition?: (item: Record<string, any>) => boolean
}>;

type RowProps = {
    columns: Columns;
    checkable?: (id: number | string) => Record<string, any>;
    rowAttr?: (item: Record<string, any>) => Record<string, any>;
    id?: string;
    onClickRow?: React.MouseEventHandler<HTMLTableRowElement>;
    actions?: Actions;
    multiActions?: Array<{}>;
};

const Row = React.forwardRef<RowRef, RowProps & { item: Record<string, any> }>(function (props, ref) {
    const {0: item, 1: setItem} = React.useState<Record<string, any>>(props.item);
    const {0: removed, 1: setRemoved} = React.useState(false);
    let handler = React.useMemo<RowRef>(() => ({
        remove: () => setRemoved(true),
        redraw: (item: Record<string, any>) => setItem(item)
    }), [removed, item]);
    React.useImperativeHandle(ref, () => handler, [handler]);
    return (
        <tr hidden={removed} className="align-middle" onClick={e => props.onClickRow && props.onClickRow(e)} {...(props.rowAttr ? props.rowAttr(item) : {})}>
            {props.multiActions?.length || props.checkable ? (
                <th className="check">
                    <MDBCheckbox value={item[props.id || 'id']} {...(props.checkable ? props.checkable(item[props.id || 'id']) : {})} />
                </th>
            ) : null}
            {Object.keys(props.columns || {}).map(key => {
                if (props.columns[key].hidden) {
                    return null;
                }
                let value = props.columns[key].format?.(item[key], item, handler) || item[key];
                return (
                    <td key={key} className={props.columns[key].className}
                        contentEditable={props.columns[key].editable ? true : undefined}
                        onBlur={props.columns[key].editable?.bind(null, item, handler)}
                        onFocus={props.columns[key].editable ? e => e.target.innerText = (typeof value === 'string' ? value : (item[key] || '')) : undefined}>
                        {value}
                    </td>
                );
            })}
            {props.actions?.length ? (
                <td className="action">
                    {props.actions.length > 1 ? (
                        <MDBDropdown>
                            <MDBDropdownToggle size="sm"><I18N>Action</I18N></MDBDropdownToggle>
                            {(() => {
                                let children = [];
                                for (let i in props.actions) {
                                    if (typeof props.actions[i].condition !== 'function' || props.actions[i].condition!(item)) {
                                        children.push(React.createElement(MDBDropdownItem, {
                                            key: 'a-' + i,
                                            link: true,
                                            onClick: props.actions[i].onClick.bind(null, item, handler)
                                        }, props.actions[i].label));
                                    }
                                }
                                return React.createElement(MDBDropdownMenu, null, ...children);
                            })()}
                        </MDBDropdown>
                    ) : (
                        <MDBBtn size="sm" type="button" onClick={props.actions[0].onClick.bind(null, item, handler)}>{props.actions[0].label}</MDBBtn>
                    )}
                </td>
            ) : null}
        </tr>
    );
});

type GridProps = SorterProps & FilterProps & RowProps & {
    doLoad?: (params: Record<string, any>) => CancelablePromise<ListResponse<any> | string>;
    limit?: number;
    afterPager?: React.ReactNode;
    query: URLSearchParams;
    setQuery: (search: URLSearchParams) => void;
    newUrl?: string | ((data: Array<Record<string, any>>, setData: React.Dispatch<React.SetStateAction<Array<Record<string, any>>>>) => void);
    onDrop?: React.DragEventHandler<HTMLDivElement>;
}

export interface GridRef {
    reload: (params?: URLSearchParams) => void;
}

export default React.forwardRef<GridRef, GridProps>(function Grid(props, ref) {
    const {0: data, 1: setData} = React.useState<Array<Record<string, any>>>([]);
    const {0: total, 1: setTotal} = React.useState(0);
    const {0: loading, 1: setLoading} = React.useState(false);
    let navigate = useNavigate();
    const debounce = React.useRef<CancelablePromise<any> | null>(null);
    const code = React.useRef('');
    const load = React.useCallback((params: URLSearchParams) => {
        if (!props.doLoad) {
            return;
        }
        let tmp: Record<string, any> = {}, c = '';
        for (const [k, v] of params.entries()) {
            if (v) {
                tmp[k] = v;
                c += '&' + k + '=' + v;
            }
        }
        if (debounce.current) {
            if (code.current === c) {
                return;
            }
            debounce.current.cancel();
        }
        code.current = c;
        props.setQuery(params);
        setLoading(true);
        (debounce.current = props.doLoad(tmp)).then(response => {
            if (typeof response !== 'string') {
                setData(response.data);
                setTotal(response.total);
            }
            setLoading(false);
            debounce.current = null;
        });
    }, [props.doLoad, props.setQuery, setLoading, setData, setTotal]);
    React.useEffect(() => {
        load(props.query);
    }, [props.query, load]);
    React.useImperativeHandle(ref, () => ({
        reload: params => load(params || props.query)
    }), [props.query, load]);
    let filterable = React.useMemo(() => {
        for (let key in (props.columns || {})) {
            if (props.columns[key].filterable) {
                return true;
            }
        }
        return false;
    }, [props.columns]);
    React.useEffect(() => {
        if (props.newUrl) {
            let l = () => {
                if (typeof props.newUrl === 'string') {
                    navigate(props.newUrl);
                } else {
                    props.newUrl?.(data, setData);
                }
            };
            document.addEventListener('new', l);
            return () => {
                document.removeEventListener('new', l);
            };
        }
    }, [props.newUrl, navigate, data, setData]);
    return (
        <div className="grid" data-new-url={props.newUrl ? '1' : undefined} onDrop={props.onDrop} onDragOver={props.onDrop ? willSendFile : undefined}>
            {filterable || props.renderBtns || props.additionalFilter ? (
                <Filter columns={props.columns || {}} params={props.query} onSearch={load} onReset={props.onReset} additionalFilter={props.additionalFilter} renderBtns={props.renderBtns} />
            ) : null}
            <MDBCard className="grid-content my-3">
                <MDBTable hover responsive className="mb-0">
                    <MDBTableHead>
                        <Sorter columns={props.columns || {}} params={props.query} onSort={load} actions={Boolean(props.actions?.length)} multiActions={Boolean(props.multiActions?.length)} checkable={props.checkable} />
                    </MDBTableHead>
                    <MDBTableBody>
                        {data.length ? data.map((item, index) => (
                            <Row key={index + '-data-' + (item.id || JSON.stringify(item))} item={item} {...props} />
                        )) : (
                            <tr>
                                <td colSpan={Object.keys(props.columns).length + 1} className="empty">
                                    {loading ? (
                                        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
                                    ) : (<I18N>No Data to Display</I18N>)}
                                </td>
                            </tr>
                        )}
                    </MDBTableBody>
                </MDBTable>
            </MDBCard>
            <MDBCard className="pager">
                <MDBCardBody className="d-flex justify-content-between align-items-center">
                    <Pager total={total} limit={props.limit} onPage={load} search={props.query} />
                    {props.afterPager}
                </MDBCardBody>
            </MDBCard>
        </div>
    );
});
